import type { StudentGroupAttendanceQuery } from 'core/graphql';

export interface IAttendanceRow {
  lessonId: string;
  lessonName: string;
  lessonNumber: number;
  date: string | null;
  /** true present, false absent, null = the teacher has not marked it yet. */
  attendance: boolean | null;
}

export interface IMyAttendance {
  rows: IAttendanceRow[];
  /** Lessons marked present. */
  attended: number;
  /** Lessons the teacher has actually marked (present + absent). */
  marked: number;
  /** Percent of MARKED lessons attended; null while nothing is marked. */
  percent: number | null;
}

/**
 * Normalizes `myAttendance` rows and derives the headline figure.
 *
 * The percentage is deliberately computed over MARKED lessons only, not over
 * the whole course: an unmarked lesson (teacher hasn't filled the journal, or
 * it simply hasn't happened yet) is not an absence, and counting it as one
 * would show a student a scary "40%" mid-course. `percent` is null until at
 * least one lesson is marked, so the UI can say "нет данных" instead of "0%".
 */
export const transformMyAttendance = (
  data: StudentGroupAttendanceQuery | undefined,
): IMyAttendance => {
  const rows: IAttendanceRow[] = (data?.myAttendance ?? []).map((row) => ({
    lessonId: row.lessonId,
    lessonName: row.lessonName,
    lessonNumber: row.lessonNumber,
    date: row.date ?? null,
    attendance: row.attendance ?? null,
  }));

  const marked = rows.filter((row) => row.attendance !== null).length;
  const attended = rows.filter((row) => row.attendance === true).length;

  return {
    rows,
    attended,
    marked,
    percent: marked > 0 ? Math.round((attended / marked) * 100) : null,
  };
};
